/* eslint-disable prettier/prettier */
import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/Button';
import { Loader2, Plus, Trash2, Folder } from 'lucide-react';
import { FileUploadModal } from '../../components/file/FileUploadModal';
import { FileTableHeader } from '../../components/file/FileTableHeader';
import { FileRow } from '../../components/ui/FileRow';
import { FileService } from '../../services/file.service';
import { FileType } from '../../types/FileType';
import { FileEditModal } from '../../components/file/FileEditModal';
import { useNavigate } from 'react-router-dom';
import { ConfirmDeleteModal } from '../../components/file/ConfirmDeleteModal';
import { EmailIngestSuccessModal } from '../../components/file/EmailIngestSuccessModal';
import { CustomSelect } from '../../components/ui/Select';
import { Modal } from '../../components/ui/Modal';
import { FolderService } from '../../services/folder.service';
import { FolderType } from '../../types/FolderType';

export const FilesPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [files, setFiles] = useState<FileType[]>([]);
  const [fileToEdit, setFileToEdit] = useState<FileType | null>(null);
  const [fileToDelete, setFileToDelete] = useState<FileType | null>(null);
  const [isIngesting, setIsIngesting] = useState(false);
  const [isIngestSuccessOpen, setIsIngestSuccessOpen] = useState(false);
  const [selectedFileIds, setSelectedFileIds] = useState<string[]>([]);
  const [isFolderModalOpen, setIsFolderModalOpen] = useState(false);
  const [folders, setFolders] = useState<FolderType[]>([]);
  const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false);
  const [selectedMoveFolderId, setSelectedMoveFolderId] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(true);

  const navigate = useNavigate();

  const fetchFiles = async () => {
    try {
      setIsLoading(true);
      const res = await FileService.getAllFiles();
      setFiles(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Failed to fetch files:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFolders = async () => {
    const folderData = await FolderService.getAllWithHierarchy();
    setFolders(folderData);
  };

  useEffect(() => {
    fetchFiles();
  }, []);

  useEffect(() => {
    if (isFolderModalOpen) fetchFolders();
  }, [isFolderModalOpen]);

  const handleEmailIngestion = async () => {
    setIsIngesting(true);
    try {
      await FileService.triggerIngestion();
      fetchFiles();
      setIsIngestSuccessOpen(true);
    } catch (error) {
      console.error('Failed to trigger email ingestion', error);
      alert('Email ingestion failed.');
    } finally {
      setIsIngesting(false);
    }
  };

  const toggleFileSelection = (fileId: string, checked: boolean) => {
    setSelectedFileIds((prev) =>
      checked ? [...prev, fileId] : prev.filter((id) => id !== fileId),
    );
  };

  const toggleAllSelection = () => {
    if (selectedFileIds.length === files.length) {
      setSelectedFileIds([]);
    } else {
      setSelectedFileIds(files.map((f) => f.id));
    }
  };

  const handleBulkDelete = () => {
    setIsBulkDeleteOpen(true);
  };

  const confirmBulkDelete = async () => {
    await FileService.deleteMultipleFiles(selectedFileIds);
    setSelectedFileIds([]);
    setIsBulkDeleteOpen(false);
    fetchFiles();
  };

  const flattenFolders = (folders: FolderType[], prefix = ''): { label: string; value: string }[] =>
    folders.flatMap((folder) => {
      const label = prefix ? `${prefix} > ${folder.folderName}` : folder.folderName;
      const children = flattenFolders(folder.subfolders || [], label);
      return [{ label, value: folder.id }, ...children];
    });

  const folderOptions = [
    { label: 'None', value: '' },
    ...flattenFolders(folders.filter((f) => f.id !== 'uncategorized')),
  ];

  const selectedFiles = files.filter((f) => selectedFileIds.includes(f.id));

  return (
    <div>
      <h1 className="text-4xl font-bold text-center sm:pb-4 sm:text-left text-primary">Files</h1>

      <div className="flex flex-wrap items-center justify-between gap-4 ">
        <div className="flex">
          <Button
            icon={<Plus />}
            className="px-16 py-3 sm:px-6"
            onClick={() => setIsModalOpen(true)}
          >
            New
          </Button>

          <Button
            className="flex items-center gap-2 px-6 py-3 ml-5"
            onClick={handleEmailIngestion}
            disabled={isIngesting}
          >
            {isIngesting && <Loader2 className="w-4 h-4 animate-spin" />}
            {isIngesting ? 'Ingesting...' : 'Trigger Email Ingest'}
          </Button>

          {selectedFileIds.length > 0 && (
            <div className="flex items-center gap-2 ml-4">
              <Button variant="ghost" onClick={handleBulkDelete}>
                <Trash2 className="w-5 h-5 text-red-500" />
              </Button>
              <Button variant="ghost" onClick={() => setIsFolderModalOpen(true)}>
                <Folder className="w-5 h-5 text-primary" />
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="mt-8">
        {isLoading ? (
          <p className="mt-6 text-xl text-center text-muted-foreground text-primary" >Loading...</p>
        ) : files.length === 0 ? (
          <p className="mt-6 text-xl text-center text-muted-foreground text-primary">No files found.</p>
        ) : (
          <>
            <FileTableHeader
              allSelected={selectedFileIds.length === files.length}
              onToggleAll={toggleAllSelection}
            />
            {files.map((file, index) => (
              <FileRow
                key={file.id}
                file={file}
                index={index}
                isSelected={selectedFileIds.includes(file.id)}
                onSelect={(checked) => toggleFileSelection(file.id, checked)}
                onView={() => navigate('/files/' + file.id)}
                onEdit={() => setFileToEdit(file)}
                onDelete={() => setFileToDelete(file)}
              />
            ))}
          </>
        )}
      </div>

      <FileUploadModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={fetchFiles}
      />

      <FileEditModal
        file={fileToEdit}
        isOpen={!!fileToEdit}
        onClose={() => setFileToEdit(null)}
        onSave={fetchFiles}
      />

      <ConfirmDeleteModal
        isOpen={!!fileToDelete}
        names={[fileToDelete?.editedName || fileToDelete?.fileName || 'Unnamed']}
        onCancel={() => setFileToDelete(null)}
        onConfirm={async () => {
          await FileService.deleteFile(fileToDelete!.id);
          setFileToDelete(null);
          fetchFiles();
        }}
      />

      <ConfirmDeleteModal
        isOpen={isBulkDeleteOpen}
        names={selectedFiles.map((f) => f.editedName || f.fileName || 'Unnamed')}
        onCancel={() => setIsBulkDeleteOpen(false)}
        onConfirm={confirmBulkDelete}
      />

      <EmailIngestSuccessModal
        isOpen={isIngestSuccessOpen}
        onClose={() => setIsIngestSuccessOpen(false)}
      />

      <Modal
        isOpen={isFolderModalOpen}
        onClose={() => {
          setIsFolderModalOpen(false);
          setSelectedMoveFolderId(undefined);
        }}
        title="Move to Folder"
      >
        <div className="space-y-6">
          <CustomSelect
            placeholder="Select a folder"
            data={folderOptions}
            value={selectedMoveFolderId || ''}
            onChange={(val) => setSelectedMoveFolderId(val)}
          />
          <div className="flex justify-end gap-3 mt-4">
            <Button
              variant="secondary"
              onClick={() => {
                setIsFolderModalOpen(false);
                setSelectedMoveFolderId(undefined);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                const actualFolderId: string | null =
                  selectedMoveFolderId === '' ? null : (selectedMoveFolderId ?? null);

                await FileService.moveFilesToFolder(selectedFileIds, actualFolderId);

                setIsFolderModalOpen(false);
                setSelectedMoveFolderId(undefined);
                setSelectedFileIds([]);
                fetchFiles();
              }}
            >
              Save
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
